import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, ArrowRight, Check } from "lucide-react";
import { trackInitiateCheckout, prepareStripeCheckout } from "@/lib/metaPixel";
import { STRIPE_VIP_URL, EVENT_SOLD_OUT } from "@/lib/tickets";
import useCapacity from "@/lib/useCapacity";

const STORAGE_KEY = "varna_vip_seen_v1";
const DELAY_MS = 8000;
const VIP_URL = STRIPE_VIP_URL;

const PERKS = [
  "Запазено място на първите редове",
  "ВИП Коктейл с лекторите",
  "Достъп до ВИП обществото във Viber",
  "Запис на цялото събитие",
];

export default function VipPopup() {
  const [open, setOpen] = useState(false);
  const { tier, remaining, vip_remaining } = useCapacity();
  const soldOut = EVENT_SOLD_OUT || tier === "sold_out";

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const goVip = () => {
    const checkoutUrl = prepareStripeCheckout({
      url: VIP_URL,
      ticketType: "vip",
      value: 119,
      currency: "EUR",
      contentName: "VIP билет — Popup",
    });
    trackInitiateCheckout({
      value: 119,
      currency: "EUR",
      content_name: "VIP билет — Popup",
      content_ids: ["vip-119"],
    });
    close();
    window.open(checkoutUrl, "_blank", "noopener,noreferrer");
  };

  if (soldOut) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="vip-popup"
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6 bg-[#050d1e]/70 backdrop-blur-sm"
          onClick={close}
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-lg w-full bg-gradient-to-b from-[#1c1b1a] to-[#0B1F3D] border border-[#E0B95C]/50 shadow-[0_30px_80px_-10px_rgba(224,185,92,0.45)] text-[#F5EBD9] p-8 sm:p-10 overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-1/3 -right-1/4 w-[70%] h-[100%] bg-[radial-gradient(ellipse_at_center,rgba(224,185,92,0.18),transparent_65%)]" />

            <button
              onClick={close}
              data-testid="vip-popup-close"
              aria-label="Затвори"
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-[#F5EBD9]/20 rounded-full hover:border-[#E0B95C] hover:text-[#E0B95C] transition-colors z-10"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>

            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E0B95C] to-[#F5CE72] text-[#0B1F3D] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.32em] mb-6">
              <Crown className="w-3.5 h-3.5" strokeWidth={2.5} />
              Последни VIP места
            </div>

            <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[1.05] mb-4">
              Остават само <span className="text-[#E0B95C]">{vip_remaining} VIP билета</span>.
            </h3>
            <p className="text-[#F5EBD9]/75 leading-relaxed mb-6">
              {remaining <= 50
                ? `Остават само ${remaining} билета за цялото събитие — VIP местата на първите редове свършват първи.`
                : "VIP местата на първите редове са с ограничен брой и свършват първи."}
            </p>

            <ul className="space-y-2.5 mb-8">
              {PERKS.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#F5EBD9]/85">
                  <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#E0B95C]" strokeWidth={2.5} />
                  {p}
                </li>
              ))}
            </ul>

            <div className="flex items-baseline gap-3 mb-8">
              <span className="font-display display-tight text-6xl sm:text-7xl text-[#E0B95C]">
                119
              </span>
              <span className="font-mono text-lg text-[#F5EBD9]/70">€</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5EBD9]/50 ml-2">
                включени всички такси
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={goVip}
                data-testid="vip-popup-cta"
                className="flex-1 inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#E0B95C] to-[#F5CE72] text-[#0B1F3D] px-6 py-4 rounded-full uppercase tracking-widest text-sm font-bold hover:scale-[1.02] transition-transform"
              >
                Вземи VIP билет сега
                <ArrowRight className="w-4 h-4" strokeWidth={2.8} />
              </button>
              <button
                onClick={close}
                data-testid="vip-popup-later"
                className="px-6 py-4 rounded-full border border-[#F5EBD9]/25 text-[#F5EBD9]/70 hover:text-[#F5EBD9] hover:border-[#F5EBD9]/60 transition-colors uppercase tracking-widest text-xs font-medium"
              >
                Може би по-късно
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
